import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import { ArrowLeft, X, Heart, ChatCircle, ImageSquare, LinkSimple, GraduationCap, PaperPlaneRight, Flag, ShieldWarning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { VerificationBadge, AiBadge } from "@/components/MemorialBadges";
import MemorialCardDialog from "@/components/MemorialCardDialog";
import MemorialTranslate from "@/components/MemorialTranslate";
import ShareButton from "@/components/ShareButton";

const REPORT_REASONS = [
  ["living_person", "The person is living"], ["incorrect_info", "Incorrect information"],
  ["duplicate", "Duplicate memorial"], ["unauthorized", "Unauthorized memorial"],
  ["privacy", "Privacy concern"], ["family_objection", "Family objection"],
  ["copyright", "Copyright issue"], ["inappropriate", "Inappropriate content"],
  ["harassment", "Harassment"], ["fraud", "Fraud"], ["other", "Other"],
];
const FAMILY_REQUESTS = [
  ["correction", "Correction of information"], ["add_info", "Add information"],
  ["remove_photo", "Remove a photograph"], ["privacy", "Privacy change"],
  ["manage", "Memorial management / transfer"], ["removal", "Removal review"],
];

function ReportDialog({ mid }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!reason) { toast.error("Please choose a reason"); return; }
    setBusy(true);
    try { await api.post(`/memorials/${mid}/report`, { reason, detail }); toast.success("Thank you — our team will review this."); setOpen(false); setReason(""); setDetail(""); }
    catch { toast.error("Could not submit report"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid="report-memorial-btn"><Flag size={14} /> Report / Request Correction</Button>
      </DialogTrigger>
      <DialogContent data-testid="report-dialog">
        <DialogHeader><DialogTitle>Report or request a correction</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="report-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent>{REPORT_REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Details</Label>
            <Textarea value={detail} onChange={(e) => setDetail(e.target.value)} className="min-h-[90px]" data-testid="report-detail" placeholder="Tell us what should be corrected or why you're reporting this…" />
          </div>
        </div>
        <DialogFooter><Button onClick={send} disabled={busy} className="rounded-full" data-testid="report-submit">{busy ? "Sending…" : "Submit"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FamilyDialog({ mid }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ request_type: "", relationship: "", detail: "", contact: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!f.request_type || !f.relationship.trim() || !f.detail.trim() || !f.contact.trim()) { toast.error("Please complete all fields"); return; }
    setBusy(true);
    try { await api.post(`/memorials/${mid}/family-request`, f); toast.success("Request received — we may contact you to verify."); setOpen(false); setF({ request_type: "", relationship: "", detail: "", contact: "" }); }
    catch { toast.error("Could not submit request"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="rounded-full gap-1.5 text-muted-foreground" data-testid="family-request-btn"><ShieldWarning size={14} /> Family / Authorized Request</Button>
      </DialogTrigger>
      <DialogContent data-testid="family-dialog">
        <DialogHeader><DialogTitle>Family / authorized representative request</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Request type</Label>
            <Select value={f.request_type} onValueChange={(v) => setF({ ...f, request_type: v })}>
              <SelectTrigger data-testid="family-type"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{FAMILY_REQUESTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Your relationship to the person</Label><Input value={f.relationship} onChange={(e) => setF({ ...f, relationship: e.target.value })} data-testid="family-relationship" placeholder="e.g. daughter, spouse" /></div>
          <div className="space-y-1.5"><Label>Details</Label><Textarea value={f.detail} onChange={(e) => setF({ ...f, detail: e.target.value })} className="min-h-[80px]" data-testid="family-detail" /></div>
          <div className="space-y-1.5"><Label>Contact (so we can verify)</Label><Input value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} data-testid="family-contact" placeholder="Email or phone" /></div>
        </div>
        <DialogFooter><Button onClick={send} disabled={busy} className="rounded-full" data-testid="family-submit">{busy ? "Sending…" : "Submit request"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

async function uploadPhoto(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.id;
}

function Tribute({ mid, t, user, onChange }) {
  const [showC, setShowC] = useState(false);
  const [comment, setComment] = useState("");
  const voted = user && (t.votes || []).includes(user.id);
  const initials = (t.author_name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
  const isVideo = t.link_type === "video" || /youtube|youtu\.be|vimeo/.test(t.link_url || "");

  const vote = async () => { if (!user) { toast.error("Sign in to react"); return; } await api.post(`/memorials/${mid}/tributes/${t.id}/vote`); onChange(); };
  const addComment = async () => { if (!comment.trim()) return; await api.post(`/memorials/${mid}/tributes/${t.id}/comments`, { text: comment }); setComment(""); onChange(); };

  return (
    <div className="rounded-2xl border border-border/60 bg-card p-5" data-testid={`tribute-${t.id}`}>
      <div className="flex items-center gap-3">
        <Avatar className="h-9 w-9"><AvatarImage src={fileUrl(t.author_avatar)} /><AvatarFallback className="bg-accent text-xs">{initials}</AvatarFallback></Avatar>
        <div><p className="text-sm font-medium">{t.author_name}</p><p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</p></div>
      </div>
      {t.text && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed">{t.text}</p>}
      {t.photo_file_id && <img src={fileUrl(t.photo_file_id)} alt="tribute" className="mt-3 max-h-96 w-full rounded-xl object-cover" />}
      {t.link_url && (
        <a href={t.link_url} target="_blank" rel="noreferrer" className="mt-3 flex items-center gap-2 rounded-xl border border-border/60 bg-muted/30 p-3 text-sm text-secondary hover:underline">
          <LinkSimple size={16} /> {isVideo ? "Watch video" : "Open link"} — <span className="truncate text-muted-foreground">{t.link_url}</span>
        </a>
      )}
      <div className="mt-4 flex items-center gap-5 text-sm">
        <button onClick={vote} data-testid={`tribute-vote-${t.id}`} className={`flex items-center gap-1.5 transition-colors ${voted ? "text-secondary" : "text-muted-foreground hover:text-foreground"}`}>
          <Heart size={17} weight={voted ? "fill" : "regular"} /> {(t.votes || []).length}
        </button>
        <button onClick={() => setShowC((s) => !s)} className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground">
          <ChatCircle size={17} /> {(t.comments || []).length}
        </button>
      </div>
      {showC && (
        <div className="mt-3 space-y-2 border-t border-border/60 pt-3">
          {(t.comments || []).map((c) => (
            <div key={c.id} className="text-sm"><span className="font-medium">{c.author_name}: </span><span className="text-muted-foreground">{c.text}</span></div>
          ))}
          {user && (
            <div className="flex gap-2 pt-1">
              <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Write a comment…" className="rounded-full" data-testid={`comment-input-${t.id}`} onKeyDown={(e) => { if (e.key === "Enter") addComment(); }} />
              <Button size="icon" className="h-9 w-9 shrink-0 rounded-full" onClick={addComment} data-testid={`comment-send-${t.id}`}><PaperPlaneRight size={15} /></Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MemorialDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [text, setText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [photoId, setPhotoId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tr, setTr] = useState(null);

  const load = () => api.get(`/public/memorials/${id}`).then((r) => setM(r.data)).catch(() => setM(false));
  useEffect(() => { load(); }, [id]);
  useSeo(m ? `In memory of ${m.name}` : "In Memoriam", "");

  if (m === null) return <div className="p-16 text-center text-muted-foreground">Loading…</div>;
  if (m === false) return <div className="p-16 text-center text-muted-foreground">Memorial not found.</div>;
  const years = [m.dob, m.dop].filter(Boolean).join(" – ");
  const fx = (k) => (tr && tr[k]) || m[k];

  const onPhoto = async (e) => { const f = e.target.files?.[0]; if (f) { try { setPhotoId(await uploadPhoto(f)); toast.success("Photo attached"); } catch { toast.error("Upload failed"); } } };
  const post = async () => {
    if (!user) { toast.error("Sign in to leave a tribute"); return; }
    if (!text.trim() && !photoId && !linkUrl.trim()) { toast.error("Add a message, photo or link"); return; }
    setBusy(true);
    try {
      const link_type = /youtube|youtu\.be|vimeo|\.mp4/.test(linkUrl) ? "video" : (linkUrl ? "web" : null);
      await api.post(`/memorials/${id}/tributes`, { text, photo_file_id: photoId, link_url: linkUrl || null, link_type });
      setText(""); setLinkUrl(""); setPhotoId(null); load();
      toast.success("Tribute shared");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-5 py-12" data-testid="memorial-detail">
      <div className="mb-6 flex items-center justify-between">
        <Link to="/memorials" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> In Memoriam</Link>
        <button onClick={() => (window.history.length > 2 ? nav(-1) : nav("/memorials"))} aria-label="Close" data-testid="memorial-close-btn" className="grid h-9 w-9 place-items-center rounded-full border border-border/60 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"><X size={18} /></button>
      </div>

      <div className="rounded-[2rem] border border-border/60 bg-card p-8 text-center shadow-sm">
        <p className="overline text-secondary mb-4">Forever in our hearts</p>
        <div className="mx-auto mb-5 h-36 w-36 overflow-hidden rounded-full ring-4 ring-secondary/20">
          {m.photo_file_id ? <img src={fileUrl(m.photo_file_id)} alt={m.name} className="h-full w-full object-cover" /> : <span className="grid h-full w-full place-items-center bg-accent text-4xl font-heading text-primary">{(m.name || "?")[0]}</span>}
        </div>
        <h1 className="font-heading text-3xl font-medium">{m.name}</h1>
        {years && <p className="mt-1 text-muted-foreground">{years}</p>}
        {(m.city || m.country) && <p className="mt-0.5 text-sm text-muted-foreground">{[m.city, m.country].filter(Boolean).join(", ")}</p>}
        <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
          <VerificationBadge status={m.verification_status || "submitted"} />
          <AiBadge generated={m.ai_generated} assisted={m.ai_assisted} />
        </div>
        {(m.class_batch || m.graduation_year || m.institution) && (
          <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground"><GraduationCap size={15} /> {[m.class_batch || m.graduation_year, m.institution].filter(Boolean).join(" · ")}</p>
        )}
        {fx("profession") && <p className="mt-1 text-sm text-muted-foreground">{fx("profession")}</p>}
        {fx("epitaph") && <p className="mx-auto mt-5 max-w-xl font-heading text-lg font-light italic text-foreground/80">“{fx("epitaph")}”</p>}
        {fx("bio") && <p className="mx-auto mt-5 max-w-xl whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{fx("bio")}</p>}
        {fx("achievements") && <p className="mx-auto mt-3 max-w-xl whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{fx("achievements")}</p>}
        {m.group_name && <p className="mt-5 text-xs text-muted-foreground">Remembered by {m.group_name}</p>}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-center gap-2" data-testid="memorial-actions">
        <MemorialCardDialog m={m} />
        <ShareButton entityType="memorial" entityId={id} targetPath={`/memorials/${id}`} title={m.name} label="Share / QR" />
        <ReportDialog mid={id} />
        <FamilyDialog mid={id} />
      </div>
      <div className="mt-3 flex justify-center">
        <MemorialTranslate mid={id} translated={!!tr} onTranslate={(fields) => setTr(fields)} onReset={() => setTr(null)} />
      </div>

      <div className="mt-10">
        <h2 className="font-heading text-2xl font-light tracking-tight">Tributes &amp; memories</h2>
        <p className="mt-1 text-sm text-muted-foreground">Share a memory, a photo, or a link. Others can react and comment.</p>

        {user ? (
          <div className="mt-5 rounded-2xl border border-border/60 bg-card p-4" data-testid="tribute-composer">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Share a memory…" className="min-h-[80px] resize-none border-0 px-1 focus-visible:ring-0" data-testid="tribute-text" />
            {photoId && <img src={fileUrl(photoId)} alt="attached" className="mt-2 max-h-56 rounded-xl object-cover" />}
            <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="Add a link (web page or video)…" className="mt-2 rounded-full text-sm" data-testid="tribute-link" />
            <div className="mt-3 flex items-center justify-between">
              <label className="flex cursor-pointer items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="tribute-photo-label">
                <ImageSquare size={18} /> Photo <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
              </label>
              <Button onClick={post} disabled={busy} className="rounded-full" data-testid="tribute-submit">{busy ? "Posting…" : "Post tribute"}</Button>
            </div>
          </div>
        ) : (
          <p className="mt-5 rounded-2xl border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
            <Link to="/login" className="text-secondary hover:underline">Sign in</Link> to leave a tribute.
          </p>
        )}

        <div className="mt-6 space-y-4">
          {(m.tributes || []).length === 0 && <p className="text-center text-sm text-muted-foreground">Be the first to share a tribute.</p>}
          {[...(m.tributes || [])].reverse().map((t) => <Tribute key={t.id} mid={id} t={t} user={user} onChange={load} />)}
        </div>
      </div>
    </div>
  );
}
